/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { CircleHelp, CircleMinus, Construction, OctagonMinus } from 'lucide-react';

import { cn } from '@/utils';

export const LiveAgentStatusCheck = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-green text-darkGreen rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <svg
                className="text-darkGreen"
                xmlns="http://www.w3.org/2000/svg"
                height={(6 / 7) * size}
                viewBox="0 0 24 24"
            >
                <path
                    fill="currentColor"
                    d="m18.02 4.76 2.5 2.5-9.93 9.94c-.69.7-1.81.7-2.5 0l-3.95-3.94L2.88 12 5.4 9.49l3.95 3.94 7.42-7.42 1.26-1.25Z"
                />
            </svg>
        </div>
    );
};

export const LiveAgentStatusHelp = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-slate-300 text-slate-700 rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <CircleHelp size={(4 / 5) * size} className="shrink-0" />
        </div>
    );
};

export const LiveAgentStatusWarning = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-danger text-[hsla(336,100%,94%,1)] rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <svg xmlns="http://www.w3.org/2000/svg" height={(6 / 7) * size} viewBox="0 0 24 24">
                <path fill="currentColor" d="M17.85 10.22a1.78 1.78 0 0 1 0 3.56H6.8a1.78 1.78 0 1 1 0-3.56h11.05Z" />
            </svg>
        </div>
    );
};

export const LiveAgentStatusCanceled = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-slate-300 text-slate-700 rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <OctagonMinus size={(4 / 5) * size} className="shrink-0" />
        </div>
    );
};

export const LiveAgentStatusPending = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-slate-200 text-slate-400 rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <svg xmlns="http://www.w3.org/2000/svg" height={(6 / 7) * size} viewBox="0 0 24 24">
                <path
                    fill="currentColor"
                    d="M11.94 5.48c4.1 0 7.08 2.09 8.81 6.13a1 1 0 0 1 0 .78c-1.73 4.04-4.71 6.13-8.8 6.13-4.1 0-7.08-2.09-8.81-6.13a1 1 0 0 1 0-.78c1.73-4.04 4.71-6.13 8.8-6.13Zm0 2c-3.07 0-5.26 1.4-6.7 4.33l-.09.19.1.2c1.38 2.82 3.48 4.23 6.4 4.32h.3c3.07 0 5.26-1.4 6.7-4.33l.09-.2-.1-.18c-1.38-2.83-3.48-4.24-6.4-4.33h-.3Zm0 .97a3.55 3.55 0 1 1 0 7.1 3.55 3.55 0 0 1 0-7.1Zm0 2.37a1.18 1.18 0 1 0 0 2.36 1.18 1.18 0 0 0 0-2.36Z"
                />
            </svg>
        </div>
    );
};

export const LiveAgentStatusDismissed = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-slate-200 text-slate-400 rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <CircleMinus className="text-slate-400" size={(4 / 5) * size} />
        </div>
    );
};

export const LiveAgentStatusDisabled = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-slate-200 text-slate-400 rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <svg
                className="text-slate-400"
                xmlns="http://www.w3.org/2000/svg"
                height={(6 / 7) * size}
                viewBox="0 0 24 24"
            >
                <path
                    fill="currentColor"
                    d="M5.26 5.17a2.96 2.96 0 0 1 4.19 0l2.64 2.65 2.64-2.65a2.96 2.96 0 0 1 4.04-.13l.14.13a2.96 2.96 0 0 1 0 4.19L16.27 12l2.64 2.64a2.96 2.96 0 0 1 .14 4.04l-.14.15a2.96 2.96 0 0 1-4.18 0l-2.64-2.65-2.64 2.65a2.96 2.96 0 0 1-4.05.13l-.14-.13a2.96 2.96 0 0 1 0-4.19L7.9 12 5.26 9.36a2.96 2.96 0 0 1-.13-4.04l.13-.15Z"
                />
            </svg>
        </div>
    );
};

export const LiveAgentStatusDraft = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-slate-200 text-slate-400 rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <Construction className="text-slate-400" size={(4 / 5) * size} />
        </div>
    );
};

export const LiveAgentStatusMaintenance = ({ size = 20, className = '' }) => {
    return (
        <div
            style={{ width: size, height: size }}
            className={cn(
                'bg-slate-200 text-slate-400 rounded h-[20px] w-[20px] flex items-center justify-center shrink-0',
                className,
            )}
        >
            <svg
                className="text-slate-400"
                xmlns="http://www.w3.org/2000/svg"
                height={(6 / 7) * size}
                viewBox="0 0 24 24"
            >
                <path
                    fill="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.78"
                    d="M13.74 7.9a.79.79 0 0 0 0 1.1L15 10.26a.79.79 0 0 0 1.1 0l2.98-2.97a4.74 4.74 0 0 1-6.27 6.27L7.36 19a1.67 1.67 0 1 1-2.37-2.37l5.45-5.45a4.74 4.74 0 0 1 6.27-6.27L13.74 7.9Z"
                    opacity=".99"
                />
            </svg>
        </div>
    );
};
